/**
 * Fias Leads Arche Page — route wrapper for the sales CRM arche (Phase 9).
 *
 * Mounted at /a/arche_fias_leads/*. Holder-scoped: every sub-page reads
 * data via the shared <HolderSwitcher> selection (Personal + Org both
 * supported; OQ-9.2 left this open to either). Until the user has
 * picked a Holder, the arche shows an empty-state.
 *
 * Sub-routes mirror the Fias 360 layout:
 *   /                     → DashboardPage
 *   /companies            → CompaniesListPage
 *   /companies/:companyId → CompanyDetailPage
 *   /contacts             → ContactsListPage
 *   /contacts/:contactId  → ContactDetailPage
 *   /deals                → DealsKanbanPage (default deals view)
 *   /deals/list           → DealsListPage
 *   /deals/:dealId        → DealDetailPage
 *   /tasks                → TasksPage
 *   /activities           → ActivitiesPage
 *   /templates            → EmailTemplatesPage
 *   /reports              → ReportsPage
 *   /import               → ImportPage
 *   /settings             → SettingsHomePage (sub-nav to Pipelines /
 *                            Automations / CustomFields / SavedViews)
 *   /settings/pipelines   → PipelinesSettingsPage
 *   /settings/automations → AutomationsPage
 *   /settings/custom-fields → CustomFieldsSettingsPage
 *   /settings/saved-views → SavedViewsSettingsPage
 */

import { Routes, Route } from 'react-router-dom';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { ARCHE_ID } from '../constants';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { FiasLeadsNav } from '../components/FiasLeadsNav';
import { FiasLeadsGlobalSearch } from '../components/FiasLeadsGlobalSearch';
import { CompaniesListPage } from './CompaniesListPage';
import { CompanyDetailPage } from './CompanyDetailPage';
import { ContactsListPage } from './ContactsListPage';
import { ContactDetailPage } from './ContactDetailPage';
import { DealsKanbanPage } from './DealsKanbanPage';
import { DealsListPage } from './DealsListPage';
import { DealDetailPage } from './DealDetailPage';
import { PipelinesSettingsPage } from './PipelinesSettingsPage';
import { TasksPage } from './TasksPage';
import { ActivitiesPage } from './ActivitiesPage';
import { EmailTemplatesPage } from './EmailTemplatesPage';
import { SettingsHomePage } from './SettingsHomePage';
import { AutomationsSettingsPage } from './AutomationsSettingsPage';
import { CustomFieldsSettingsPage } from './CustomFieldsSettingsPage';
import { SavedViewsSettingsPage } from './SavedViewsSettingsPage';
import { DashboardPage } from './DashboardPage';
import { ReportsPage } from './ReportsPage';
import { ImportExportPage } from './ImportExportPage';

function NoHolderEmptyState({
  holderId,
  setHolderId,
}: {
  holderId: string | null;
  setHolderId: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <h2 className="text-xl font-semibold text-foreground">Choose a workspace to get started</h2>
      <p className="max-w-md text-muted-foreground">
        Fias Leads is your sales CRM — companies, contacts, deals, pipelines, tasks. Personal Holder
        for solo users, Org Holder for teams. Pick one to begin.
      </p>
      <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
    </div>
  );
}

function FiasLeadsArcheContent() {
  const { holderId, setHolderId } = useSelectedHolder();

  return (
    <ArcheLayout>
      <div className="flex flex-col h-full">
        <ArcheHeader archeId={ARCHE_ID} title="Fias Leads">
          {holderId && <FiasLeadsGlobalSearch holderId={holderId} />}
          <HolderSwitcher selectedHolderId={holderId} onChange={(id) => setHolderId(id)} />
        </ArcheHeader>
        {holderId && <FiasLeadsNav />}
        <div className="flex-1 overflow-auto">
          {!holderId ? (
            <NoHolderEmptyState holderId={holderId} setHolderId={setHolderId} />
          ) : (
            <Routes>
              <Route path="/" element={<DashboardPage holderId={holderId} />} />
              <Route path="/companies" element={<CompaniesListPage holderId={holderId} />} />
              <Route
                path="/companies/:companyId"
                element={<CompanyDetailPage holderId={holderId} />}
              />
              <Route path="/contacts" element={<ContactsListPage holderId={holderId} />} />
              <Route
                path="/contacts/:contactId"
                element={<ContactDetailPage holderId={holderId} />}
              />
              <Route path="/deals" element={<DealsKanbanPage holderId={holderId} />} />
              <Route path="/deals/list" element={<DealsListPage holderId={holderId} />} />
              <Route path="/deals/:dealId" element={<DealDetailPage holderId={holderId} />} />
              <Route path="/tasks" element={<TasksPage holderId={holderId} />} />
              <Route path="/activities" element={<ActivitiesPage holderId={holderId} />} />
              <Route path="/templates" element={<EmailTemplatesPage holderId={holderId} />} />
              <Route path="/reports" element={<ReportsPage holderId={holderId} />} />
              <Route path="/import" element={<ImportExportPage holderId={holderId} />} />
              <Route
                path="/settings/pipelines"
                element={<PipelinesSettingsPage holderId={holderId} />}
              />
              <Route
                path="/settings/automations"
                element={<AutomationsSettingsPage holderId={holderId} />}
              />
              <Route
                path="/settings/custom-fields"
                element={<CustomFieldsSettingsPage holderId={holderId} />}
              />
              <Route
                path="/settings/saved-views"
                element={<SavedViewsSettingsPage holderId={holderId} />}
              />
              <Route path="/settings" element={<SettingsHomePage holderId={holderId} />} />
            </Routes>
          )}
        </div>
      </div>
    </ArcheLayout>
  );
}

export function FiasLeadsArchePage() {
  return <FiasLeadsArcheContent />;
}
